/**
 * ToolbarButton — shared spec for the floating controls over the 3D viewports.
 *
 * Centralizes the base sizing (fixed h-9, consistent padding/typography/icon
 * rhythm) and the neutral "off" glass state, while letting each button keep its
 * own accent color. Use `always` for actions that are permanently lit (Stats,
 * Pricing), `active` for toggles, and `fullWidth` for a stacked overflow menu.
 *
 * Style constants live in ./toolbarStyles so this file stays component-only.
 */
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { TOOLBAR_BASE, TOOLBAR_OFF, TOOLBAR_ON, type ToolbarAccent } from './toolbarStyles'

interface ToolbarButtonProps {
  icon: ReactNode
  /** Omit for an icon-only square button. */
  label?: string
  /** Active accent color. */
  accent?: ToolbarAccent
  /** Toggle state — lit accent vs neutral off. */
  active?: boolean
  /** Permanently lit actions (Stats/Pricing). */
  always?: boolean
  /** Stretch + left-align for the stacked overflow menu. */
  fullWidth?: boolean
  onClick: () => void
  title: string
  /** Optional trailing content (e.g. the Labels "%" suffix). */
  children?: ReactNode
}

export function ToolbarButton({
  icon,
  label,
  accent = 'sky',
  active,
  always,
  fullWidth,
  onClick,
  title,
  children,
}: ToolbarButtonProps) {
  const lit = always || active
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        TOOLBAR_BASE,
        // Icon-only square unless it carries a label.
        label ? '' : 'w-9 justify-center px-0',
        // Stacked menu: stretch and left-align.
        fullWidth && 'w-full justify-start px-2.5',
        lit ? TOOLBAR_ON[accent] : TOOLBAR_OFF,
      )}
    >
      {icon}
      {label}
      {children}
    </button>
  )
}

/** Thin vertical separator between toolbar groups. */
export function ToolbarDivider() {
  return <div className="mx-0.5 h-5 w-px self-center bg-white/15" />
}
